/**
 * ServerDown Error Class
 *
 * This error is thrown when the sync-box system cannot connect to the server
 * due to network issues, server unavailability, or other connectivity problems.
 */
export default class ServerDownError extends Error {
    name = 'ServerDownError';
    isServerDown = true;
    timestamp;
    retryAfter; // Optional retry delay in milliseconds
    constructor(message = 'Server is currently unavailable. Please check your connection and try again.', retryAfter) {
        super(message);
        // Set the prototype explicitly to ensure instanceof works correctly
        Object.setPrototypeOf(this, ServerDownError.prototype);
        this.timestamp = Date.now();
        this.retryAfter = retryAfter;
    }
    /**
     * Check if the error should be retried based on retryAfter timestamp
     */
    canRetry() {
        if (!this.retryAfter)
            return true;
        return Date.now() >= this.timestamp + this.retryAfter;
    }
    /**
     * Get the time until retry is allowed (in milliseconds)
     */
    getTimeUntilRetry() {
        if (!this.retryAfter)
            return 0;
        const timeUntilRetry = this.timestamp + this.retryAfter - Date.now();
        return Math.max(0, timeUntilRetry);
    }
    /**
     * Create a ServerDownError from a network error or response
     */
    static fromNetworkError(error, retryAfter = 10000) {
        let message = 'Server is currently unavailable. Please check your connection and try again.';
        if (error?.code === 'ECONNREFUSED') {
            message = 'Unable to connect to server. Server may be down or unreachable.';
        }
        else if (error?.code === 'ENOTFOUND') {
            message = 'Server not found. Please check your network connection.';
        }
        else if (error?.code === 'ETIMEDOUT') {
            message = 'Connection to server timed out. Please try again.';
        }
        else if (error?.response?.status === 503) {
            message = 'Server is temporarily unavailable. Please try again later.';
        }
        else if (error?.response?.status === 502) {
            message = 'Bad gateway. Server is experiencing issues.';
        }
        else if (error?.response?.status === 504) {
            message = 'Gateway timeout. Server is not responding.';
        }
        else if (error?.message) {
            message = `Server error: ${error.message}`;
        }
        return new ServerDownError(message, retryAfter);
    }
    /**
     * Create a ServerDownError from an HTTP response
     */
    static fromResponse(status, statusText, retryAfter = 10000) {
        let message = `Server error: ${status}`;
        if (statusText) {
            message += ` - ${statusText}`;
        }
        if (status >= 500) {
            message = 'Server is experiencing internal errors. Please try again later.';
        }
        else if (status === 404) {
            message = 'Server endpoint not found.';
        }
        else if (status === 403) {
            message = 'Access to server is forbidden.';
        }
        else if (status === 401) {
            message = 'Authentication required to access server.';
        }
        return new ServerDownError(message, retryAfter);
    }
}
